import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  ExternalLink,
  FolderInput,
  GitCompare,
  Pencil,
  Trash2,
} from "lucide-react";
import clsx from "clsx";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { useMediaQuery } from "hooks/useMediaQuery";
import { useSearchParamState } from "hooks/useSearchParamState";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { FolderRow } from "../FolderRow";
import { FolderSuggestionModal } from "../FolderSuggestionModal";
import { MovePlanToFolderModal } from "../MovePlanToFolderModal";
import { PlanComparisonModal } from "../PlanComparisonModal";
import { RenameItemModal } from "../RenameItemModal";
import { ONBOARDING_ANCHORS } from "../Onboarding/onboardingSelectors";
import { useArchivePlan } from "../hooks/useArchivePlan";
import { useComparePlan } from "../hooks/useComparePlan";
import { useDeletePlan } from "../hooks/useDeletePlan";
import { useFolderActions } from "../hooks/useFolderActions";
import { useInitialSidebarLanding } from "../hooks/useInitialSidebarLanding";
import { useMovePlanToFolder } from "../hooks/useMovePlanToFolder";
import { useRenamePlan } from "../hooks/useRenamePlan";
import { buildSidebarRows } from "../utils/buildSidebarRows";
import { isSidebarSortMode, sortSidebarItems } from "../utils/sortSidebarItems";
import { SidebarEmptyState } from "./SidebarEmptyState";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarItemRow } from "./SidebarItemRow";
import { SidebarRailButton } from "./SidebarRailButton";
import { SidebarSearchField } from "./SidebarSearchField";
import { useFolderAccordion } from "./hooks/useFolderAccordion";
import { useSidebarSearch } from "./hooks/useSidebarSearch";
import { SMALL_SCREEN_QUERY } from "./constants";
import {
  buildFloorPlanTabUrl,
  pickEmptyStateVariant,
  toDiagramSidebarItems,
  toPlanSidebarItems,
} from "./utils";
import type { PlanRowActionsMenuItem } from "../PlanRowActionsMenu";
import type { SidebarSortMode } from "../utils/sortSidebarItems";
import type { PlanFilterMode, SidebarMode } from "../utils/types";
import type { SidebarItem, SmartSurveyorSidebarProps } from "./types";

export const SmartSurveyorSidebar = ({
  sidebarMode,
  isCollapsed: controlledCollapsed,
  onCollapsedChange,
  onSidebarModeChange,
  isTraversing,
  planRecords,
  selectedPlanRecordIndex,
  pendingPlanRecordIndex,
  onSelectPlan,
  onClearPlanSelection,
  diagramRecords,
  selectedDiagramRecordIndex,
  pendingDiagramRecordIndex,
  onSelectDiagram,
  onPlanRenamed,
  onPlanArchivedAtChanged,
  onPlanDeleted,
  folderRecords,
  onPlanFolderChanged,
  onFoldersCreated,
  onFolderRenamed,
  onFolderDeleted,
  projectId,
  onFoldersImported,
}: SmartSurveyorSidebarProps) => {
  const { t } = useTranslation();

  const [ownCollapsed, setOwnCollapsed] = useState(false);
  const [isFolderSuggestionOpen, setIsFolderSuggestionOpen] = useState(false);
  const isCollapsed = controlledCollapsed ?? ownCollapsed;
  const toggleCollapse = () => {
    const next = !isCollapsed;
    setOwnCollapsed(next);
    onCollapsedChange?.(next);
  };
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN_QUERY });

  const selectedIndex =
    sidebarMode === "plans"
      ? selectedPlanRecordIndex
      : selectedDiagramRecordIndex;

  const pendingIndex =
    sidebarMode === "plans"
      ? pendingPlanRecordIndex
      : pendingDiagramRecordIndex;

  const [sortMode, setSortMode] = useSearchParamState<SidebarSortMode>({
    key: "sidebarSort",
    defaultValue: "default",
    parse: (value) => (isSidebarSortMode(value) ? value : "default"),
    serialize: (value) => value,
    replace: true,
  });

  const [planFilter, setPlanFilter] = useSearchParamState<PlanFilterMode>({
    key: "planFilter",
    defaultValue: "active",
    parse: (value) => (value === "archived" ? "archived" : "active"),
    serialize: (value) => value,
    replace: true,
  });

  const { openFolderId, toggleFolder, isSelectedPlanFolderOpen } =
    useFolderAccordion({ planRecords, selectedPlanRecordIndex });

  const selectedPlanRecord =
    selectedPlanRecordIndex === null
      ? null
      : (planRecords[selectedPlanRecordIndex] ?? null);

  // An archived selection is not listed under the Active tab's folders, so it marks none.
  const selectedActivePlanFolderId =
    selectedPlanRecord !== null && selectedPlanRecord.archivedAt === null
      ? selectedPlanRecord.folderId
      : null;

  const {
    isRenamingOpen,
    isRenamingLoading,
    renamingPlanLabel,
    openRenamePlan,
    closeRenamePlan,
    submitRenamePlan,
  } = useRenamePlan({ planRecords, onPlanRenamed });

  const items = useMemo<SidebarItem[]>(
    () =>
      sidebarMode === "plans"
        ? toPlanSidebarItems({ planRecords, planFilter })
        : toDiagramSidebarItems({ diagramRecords }),
    [sidebarMode, planRecords, planFilter, diagramRecords],
  );

  const sortedItems = useMemo(
    () => sortSidebarItems({ items, mode: sortMode }),
    [items, sortMode],
  );

  const {
    normalizedSearchQuery,
    isSearching,
    searchedItems,
    setNormalizedSearchQuery,
  } = useSidebarSearch({ sortedItems, sidebarMode, isCollapsed });

  const isFolderView = sidebarMode === "plans" && planFilter === "active";

  const { rows, orderedItems } = useMemo(
    () =>
      buildSidebarRows({
        sortedItems: searchedItems,
        // The Archived tab and diagrams stay flat.
        folderRecords: isFolderView ? folderRecords : [],
        openFolderId,
        isSearching,
      }),
    [searchedItems, isFolderView, folderRecords, openFolderId, isSearching],
  );

  const openFolderSuggestion = useCallback(() => {
    setIsFolderSuggestionOpen(true);
  }, []);

  const closeFolderSuggestion = useCallback(() => {
    setIsFolderSuggestionOpen(false);
  }, []);

  const handleFolderToggle = useCallback(
    (folderId: number) => {
      if (isSearching) return;
      toggleFolder(folderId);
    },
    [isSearching, toggleFolder],
  );

  const selectSuccessorPlan = useCallback(
    (recordIndex: number) => {
      if (recordIndex !== selectedPlanRecordIndex) return;
      const removedPosition = orderedItems.findIndex(
        (item) => item.index === recordIndex,
      );
      if (removedPosition < 0) return;
      const successor =
        orderedItems[removedPosition + 1] ??
        orderedItems[removedPosition - 1] ??
        // A search can leave no other match, so fall back to the rest of the tab.
        sortedItems.find((item) => item.index !== recordIndex);
      if (successor) {
        onSelectPlan(successor.index);
      } else {
        onClearPlanSelection();
      }
    },
    [
      selectedPlanRecordIndex,
      orderedItems,
      sortedItems,
      onSelectPlan,
      onClearPlanSelection,
    ],
  );

  const handlePlanArchivedAtChanged = useCallback(
    (recordIndex: number, archivedAt: string | null) => {
      if (archivedAt !== null) selectSuccessorPlan(recordIndex);
      onPlanArchivedAtChanged?.(recordIndex, archivedAt);
    },
    [selectSuccessorPlan, onPlanArchivedAtChanged],
  );

  const handlePlanDeleted = useCallback(
    (recordIndex: number) => {
      selectSuccessorPlan(recordIndex);
      onPlanDeleted?.(recordIndex);
    },
    [selectSuccessorPlan, onPlanDeleted],
  );

  const {
    isArchiveConfirmOpen,
    archivingPlanLabel,
    openArchivePlan,
    closeArchivePlan,
    confirmArchivePlan,
    unarchivePlan,
  } = useArchivePlan({
    planRecords,
    onPlanArchivedAtChanged: handlePlanArchivedAtChanged,
  });

  const {
    isDeleteConfirmOpen,
    deletingPlanLabel,
    openDeletePlan,
    closeDeletePlan,
    confirmDeletePlan,
  } = useDeletePlan({ planRecords, onPlanDeleted: handlePlanDeleted });

  const { comparisonBasePlanRecord, openPlanComparison, closePlanComparison } =
    useComparePlan({ planRecords });

  const {
    movingPlanRecord,
    isCreatingFolder,
    isMoving,
    openMovePlan,
    closeMovePlan,
    createFolder,
    submitMovePlan,
  } = useMovePlanToFolder({
    planRecords,
    onPlanFolderChanged,
    onFoldersCreated,
  });

  const {
    renamingFolder,
    isRenameSaving,
    deletingFolder,
    openRenameFolder,
    closeRenameFolder,
    submitRenameFolder,
    openDeleteFolder,
    closeDeleteFolder,
    confirmDeleteFolder,
  } = useFolderActions({ folderRecords, onFolderRenamed, onFolderDeleted });

  const getFolderActionItems = (folderId: number): PlanRowActionsMenuItem[] => [
    ...(onFolderRenamed
      ? [
          {
            key: "rename",
            label: t("smartSurveyor.folders.rename"),
            icon: <Pencil size={14} />,
            onSelect: () => openRenameFolder(folderId),
          },
        ]
      : []),
    ...(onFolderDeleted
      ? [
          {
            key: "delete",
            label: t("smartSurveyor.folders.delete"),
            icon: <Trash2 size={14} />,
            onSelect: () => openDeleteFolder(folderId),
            isDestructive: true,
          },
        ]
      : []),
  ];

  const getPlanActionItems = (item: SidebarItem): PlanRowActionsMenuItem[] => [
    {
      key: "openInNewTab",
      label: t("smartSurveyor.planActions.openInNewTab"),
      icon: <ExternalLink size={14} />,
      onSelect: () => {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PLAN_OPENED_IN_NEW_TAB, {
          floorPlanId: String(item.key),
          trigger: "menu",
        });
        window.open(
          buildFloorPlanTabUrl(item.key),
          "_blank",
          "noopener,noreferrer",
        );
      },
    },
    {
      key: "compare",
      label: t("smartSurveyor.planActions.compare"),
      icon: <GitCompare size={14} />,
      onSelect: () => openPlanComparison(item.index),
    },
    ...(onPlanRenamed
      ? [
          {
            key: "rename",
            label: t("smartSurveyor.renamePlan.title"),
            icon: <Pencil size={14} />,
            onSelect: () => openRenamePlan(item.index),
          },
        ]
      : []),
    // Archived plans keep their folder but are only moved from the Active tab.
    ...(onPlanFolderChanged && !item.isArchived
      ? [
          {
            key: "moveToFolder",
            label: t("smartSurveyor.planActions.moveToFolder"),
            icon: <FolderInput size={14} />,
            onSelect: () => openMovePlan(item.key),
          },
        ]
      : []),
    ...(onPlanArchivedAtChanged
      ? [
          item.isArchived
            ? {
                key: "unarchive",
                label: t("smartSurveyor.planActions.unarchive"),
                icon: <ArchiveRestore size={14} />,
                onSelect: () => unarchivePlan(item.index),
              }
            : {
                key: "archive",
                label: t("smartSurveyor.planActions.archive"),
                icon: <Archive size={14} />,
                onSelect: () => openArchivePlan(item.index),
              },
        ]
      : []),
    ...(onPlanDeleted
      ? [
          {
            key: "delete",
            label: t("smartSurveyor.planActions.delete"),
            icon: <Trash2 size={14} />,
            onSelect: () => openDeletePlan(item.index),
            isDestructive: true,
          },
        ]
      : []),
  ];

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLUListElement,
    HTMLLIElement
  >({
    // The folder and search flags re-run the scroll when the selected plan's row comes back into the list.
    resetDep: `${sidebarMode}-${selectedIndex}-${sortMode}-${planFilter}-${isSelectedPlanFolderOpen}-${isSearching}`,
    options: { block: "center" },
  });

  const onSelect = sidebarMode === "plans" ? onSelectPlan : onSelectDiagram;

  const handleSidebarModeClick = useCallback(
    (nextMode: SidebarMode) => {
      if (nextMode !== sidebarMode) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_SIDEBAR_MODE_CHANGED, {
          mode: nextMode,
        });
      }
      onSidebarModeChange(nextMode);
    },
    [sidebarMode, onSidebarModeChange],
  );

  const handleItemClick = useCallback(
    (item: SidebarItem) => {
      if (item.index !== selectedIndex) {
        if (sidebarMode === "plans") {
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PLAN_SELECTED, {
            floorPlanId: String(item.key),
          });
        } else {
          trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_DIAGRAM_SELECTED, {
            diagramId: String(item.key),
          });
        }
      }
      onSelect(item.index);
    },
    [selectedIndex, sidebarMode, onSelect],
  );

  const handleLinkOpenedInNewTab = useCallback((item: SidebarItem) => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PLAN_OPENED_IN_NEW_TAB, {
      floorPlanId: String(item.key),
      trigger: "link",
    });
  }, []);

  const emptyStateVariant = pickEmptyStateVariant({
    sidebarMode,
    planFilter,
    rowCount: rows.length,
    hasPlans: planRecords.length > 0,
    isSearching,
  });

  useEffect(() => {
    if (isTraversing && isSmallScreen) {
      setOwnCollapsed(true);
      onCollapsedChange?.(true);
    }
  }, [isTraversing, isSmallScreen, onCollapsedChange]);

  useInitialSidebarLanding({
    sidebarMode,
    planRecords,
    selectedPlanRecordIndex,
    diagramRecords,
    onPlanFilterChange: setPlanFilter,
    onSidebarModeChange,
  });

  return (
    <>
      <RenameItemModal
        isOpen={isRenamingOpen}
        onClose={closeRenamePlan}
        title={t("smartSurveyor.renamePlan.title")}
        placeholder={t("smartSurveyor.renamePlan.placeholder")}
        currentName={renamingPlanLabel}
        onRename={submitRenamePlan}
        isLoading={isRenamingLoading}
      />
      <ConfirmActionModal
        isOpen={isArchiveConfirmOpen}
        onClose={closeArchivePlan}
        onConfirm={confirmArchivePlan}
        action="archive"
        title={t("smartSurveyor.planActions.archiveTitle")}
        description={t("smartSurveyor.planActions.archiveDescription", {
          name: archivingPlanLabel,
        })}
      />
      <ConfirmActionModal
        isOpen={isDeleteConfirmOpen}
        onClose={closeDeletePlan}
        onConfirm={confirmDeletePlan}
        action="delete"
        title={t("smartSurveyor.planActions.deleteTitle")}
        description={t("smartSurveyor.planActions.deleteDescription", {
          name: deletingPlanLabel,
        })}
      />
      <MovePlanToFolderModal
        planRecord={movingPlanRecord}
        folderRecords={folderRecords}
        isCreatingFolder={isCreatingFolder}
        isMoving={isMoving}
        onClose={closeMovePlan}
        onCreateFolder={createFolder}
        onMove={submitMovePlan}
      />
      <RenameItemModal
        isOpen={renamingFolder !== null}
        onClose={closeRenameFolder}
        title={t("smartSurveyor.folders.rename")}
        placeholder={t("smartSurveyor.folders.renamePlaceholder")}
        currentName={renamingFolder?.name ?? ""}
        onRename={submitRenameFolder}
        isLoading={isRenameSaving}
      />
      <ConfirmActionModal
        isOpen={deletingFolder !== null}
        onClose={closeDeleteFolder}
        onConfirm={confirmDeleteFolder}
        action="delete"
        title={t("smartSurveyor.folders.deleteTitle")}
        description={t("smartSurveyor.folders.deleteDescription", {
          name: deletingFolder?.name ?? "",
        })}
      />
      {onFoldersImported !== undefined && (
        <FolderSuggestionModal
          isOpen={isFolderSuggestionOpen}
          projectId={projectId}
          planRecords={planRecords}
          folderRecords={folderRecords}
          onClose={closeFolderSuggestion}
          onFoldersImported={onFoldersImported}
        />
      )}
      {comparisonBasePlanRecord !== null && (
        <PlanComparisonModal
          basePlanRecord={comparisonBasePlanRecord}
          planRecords={planRecords}
          onClose={closePlanComparison}
        />
      )}
      <div
        className={clsx(
          "relative flex-shrink-0 overflow-hidden bg-white border-r border-gray-200",
          isCollapsed ? "w-10" : "w-64",
        )}
      >
        {!isCollapsed && (
          <div className="absolute inset-y-0 left-0 w-64 flex flex-col">
            <SidebarHeader
              sidebarMode={sidebarMode}
              hasDiagrams={diagramRecords.length > 0}
              onSidebarModeChange={handleSidebarModeClick}
              onCollapse={toggleCollapse}
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              planFilter={planFilter}
              onPlanFilterChange={setPlanFilter}
              onSuggestFolders={
                onFoldersImported !== undefined && isFolderView
                  ? openFolderSuggestion
                  : undefined
              }
            />
            <SidebarSearchField
              key={sidebarMode}
              placeholder={t(
                sidebarMode === "plans"
                  ? "smartSurveyor.sidebarSearch.plansPlaceholder"
                  : "smartSurveyor.sidebarSearch.diagramsPlaceholder",
              )}
              onSearchQueryChange={setNormalizedSearchQuery}
            />
            <ul
              ref={containerRef}
              data-onboarding={ONBOARDING_ANCHORS.planList}
              className="flex-1 overflow-y-auto py-1"
            >
              {emptyStateVariant !== null && (
                <SidebarEmptyState variant={emptyStateVariant} />
              )}
              {rows.map((row) => {
                if (row.kind === "folder") {
                  return (
                    <FolderRow
                      key={`folder-${row.folder.id}`}
                      folder={row.folder}
                      planCount={row.planCount}
                      isOpen={row.isOpen}
                      containsSelectedPlan={
                        row.folder.id === selectedActivePlanFolderId
                      }
                      onToggle={handleFolderToggle}
                      actionItems={getFolderActionItems(row.folder.id)}
                    />
                  );
                }
                if (row.kind === "rootDivider") {
                  return (
                    <li
                      key="root-divider"
                      aria-hidden="true"
                      className="mx-4 my-1.5 h-px bg-gray-100"
                    />
                  );
                }
                const isSelected = selectedIndex === row.item.index;
                return (
                  <SidebarItemRow
                    key={row.item.key}
                    item={row.item}
                    position={row.position}
                    folderBranch={row.folderBranch}
                    isSelected={isSelected}
                    isPending={pendingIndex === row.item.index}
                    actionItems={
                      sidebarMode === "plans"
                        ? getPlanActionItems(row.item)
                        : []
                    }
                    href={
                      sidebarMode === "plans"
                        ? buildFloorPlanTabUrl(row.item.key)
                        : null
                    }
                    onClick={handleItemClick}
                    onLinkOpenedInNewTab={handleLinkOpenedInNewTab}
                    listItemRef={isSelected ? targetRef : undefined}
                    normalizedSearchQuery={normalizedSearchQuery}
                  />
                );
              })}
            </ul>
          </div>
        )}
        {isCollapsed && (
          <div className="absolute inset-y-0 left-0 w-10 flex flex-col items-center py-4">
            <button
              type="button"
              onClick={toggleCollapse}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
              title={t("smartSurveyor.common.showPlans")}
            >
              <ChevronRight size={16} />
            </button>
            <div className="mt-4 flex flex-col gap-1">
              {orderedItems.map((item, position) => (
                <SidebarRailButton
                  key={item.key}
                  item={item}
                  position={position}
                  isSelected={selectedIndex === item.index}
                  isPending={pendingIndex === item.index}
                  onClick={handleItemClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
